import { useEffect, useMemo, useState } from 'react';
import {
  BUCKET_LABEL,
  formatDebut,
  formatMbps,
  formatMs,
  formatShare,
  groupMarketRegions,
  MARKETS_FALLBACK,
  type MarketBucket,
  type MarketTile,
  type StarlinkMarket,
  type StarlinkMarketsPayload,
} from '@/components/value-chain/starlinkMarkets';
import { valueChainService } from '@/services/api';

type FilterId = MarketBucket | 'all';

const FILTERS: FilterId[] = ['live', 'coming_soon', 'pending', 'blocked', 'all'];

export function useStarlinkMarkets(): StarlinkMarketsPayload {
  const [payload, setPayload] = useState<StarlinkMarketsPayload>(MARKETS_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    valueChainService
      .getStarlinkMarkets()
      .then((next) => {
        if (!cancelled && next?.markets?.length) setPayload(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return payload;
}

export function StarlinkDemandMap() {
  const payload = useStarlinkMarkets();
  const [filter, setFilter] = useState<FilterId>('live');
  const [selectedIso, setSelectedIso] = useState<string | null>('US');
  const regions = useMemo(() => groupMarketRegions(payload, filter), [payload, filter]);
  const selected =
    payload.markets.find((market) => market.iso2 === selectedIso) ??
    payload.markets.find((market) => market.iso2 === 'US') ??
    payload.markets[0] ??
    null;
  const rows = chunk(regions, 3);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Starlink markets</h2>
          <p className="mt-0.5 text-[12px] text-slate-500">
            Official availability.json · debut dates · Ookla sample mix. Not a subscriber census.
          </p>
        </div>
        <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[11px]">
          {FILTERS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setFilter(item)}
              className={`rounded-md px-2.5 py-1 font-medium ${
                filter === item ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              {item === 'all' ? 'All' : BUCKET_LABEL[item]}
            </button>
          ))}
        </div>
      </div>

      <dl className="mb-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Live markets" value={payload.kpis.live} detail="available + launched" />
        <Kpi label="Coming soon" value={payload.kpis.coming_soon} detail="Starlink expected window" />
        <Kpi
          label="Subscribers"
          value={`${payload.printed.subscribers_m.toFixed(1)}M`}
          detail={`${payload.printed.subscribers_source} · ${payload.printed.subscribers_as_of}`}
        />
        <Kpi
          label="10-Q markets"
          value={payload.printed.markets_mda}
          detail={`MD&A print vs ${payload.kpis.live} live in availability.json`}
        />
      </dl>

      <div className="grid min-h-[420px] gap-3 xl:grid-cols-[minmax(0,1fr)_248px]">
        <div className="flex min-h-0 min-w-0 flex-col gap-2">
          {rows.map((row) => (
            <div key={row.map((region) => region.id).join('-')} className="flex min-h-0 flex-1 gap-2">
              {row.map((region) => (
                <div
                  key={region.id}
                  className="min-h-[168px] min-w-0"
                  style={{ flex: Math.max(region.count, 1) }}
                >
                  <RegionCard
                    label={region.label}
                    count={region.count}
                    tiles={region.tiles}
                    selectedId={selectedIso}
                    onSelect={setSelectedIso}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        <aside className="space-y-3">
          <OoklaCard payload={payload} onSelect={setSelectedIso} selectedIso={selectedIso} />
          {selected ? <SelectedMarket market={selected} /> : null}
          <p className="text-[10px] leading-relaxed text-slate-400">{payload.caveat}</p>
        </aside>
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  detail,
}: {
  label: string;
  value: number | string;
  detail: string;
}) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
      <dt className="text-[11px] text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-[20px] leading-none font-semibold tracking-tight text-slate-900">
        {value}
      </dd>
      <p className="mt-1 text-[10px] text-slate-400">{detail}</p>
    </div>
  );
}

function RegionCard({
  label,
  count,
  tiles,
  selectedId,
  onSelect,
}: {
  label: string;
  count: number;
  tiles: MarketTile[];
  selectedId: string | null;
  onSelect: (iso2: string) => void;
}) {
  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl bg-[#0d4f3c]">
      <header className="flex h-9 shrink-0 items-center justify-between gap-2 px-3 text-white">
        <h3 className="min-w-0 truncate text-[13px] font-medium">{label}</h3>
        <span className="shrink-0 text-[13px] font-semibold tabular-nums">{count}</span>
      </header>
      <div className="flex min-h-0 flex-1 flex-wrap content-stretch gap-1 p-1 pt-0">
        {tiles.map((tile) => {
          const tone = bucketTone(tile.bucket);
          const selected = tile.iso2 != null && tile.iso2 === selectedId;
          return (
            <button
              key={tile.id}
              type="button"
              title={`${tile.label} · ${BUCKET_LABEL[tile.bucket]}`}
              onClick={() => {
                if (tile.iso2) onSelect(tile.iso2);
              }}
              className="flex min-h-[72px] min-w-[92px] flex-1 flex-col justify-between overflow-hidden rounded-md p-2 text-left"
              style={{
                flex: Math.max(tile.weight, 1),
                background: tone.bg,
                color: tone.fg,
                boxShadow: selected ? 'inset 0 0 0 2px #fff' : undefined,
              }}
            >
              <span className="line-clamp-2 text-[11px] leading-tight font-semibold">
                {tile.label}
                {tile.labeled ? (
                  <span className="ml-1 rounded bg-white/20 px-1 py-px text-[8px] font-medium tracking-wide uppercase">
                    kits
                  </span>
                ) : null}
              </span>
              <span>
                <span className="block text-[13px] leading-none font-semibold">
                  {BUCKET_LABEL[tile.bucket]}
                </span>
                <span className="mt-0.5 block text-[10px] opacity-80">
                  {tile.ooklaShare != null ? formatShare(tile.ooklaShare) : tile.status.replace('_', ' ')}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function OoklaCard({
  payload,
  selectedIso,
  onSelect,
}: {
  payload: StarlinkMarketsPayload;
  selectedIso: string | null;
  onSelect: (iso2: string) => void;
}) {
  return (
    <article className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <p className="text-[11px] font-medium text-slate-400">Ookla sample mix · {payload.ookla.period}</p>
      <ul className="mt-2 space-y-1.5">
        {payload.ookla.markets.map((row) => (
          <li key={row.iso2}>
            <button
              type="button"
              onClick={() => onSelect(row.iso2)}
              className={`flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-0.5 text-left text-[12px] ${
                selectedIso === row.iso2 ? 'bg-white font-medium text-slate-900' : 'text-slate-600'
              }`}
            >
              <span>
                {row.rank}. {row.name}
              </span>
              <span className="tabular-nums text-slate-500">
                {row.sample_share != null
                  ? formatShare(row.sample_share)
                  : row.printed_subscribers_m
                    ? `${row.printed_subscribers_m.toFixed(1)}M printed`
                    : 'top 5'}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] leading-relaxed text-slate-400">{payload.ookla.caveat}</p>
    </article>
  );
}

function SelectedMarket({ market }: { market: StarlinkMarket }) {
  return (
    <article className="rounded-xl border border-slate-200 p-3">
      <p className="text-[11px] font-medium text-slate-400">Selected market</p>
      <p className="mt-1 text-[15px] font-semibold text-slate-900">{market.name}</p>
      <p className="text-[11px] text-slate-500">
        {market.region} · {BUCKET_LABEL[market.status_bucket]}
        {market.labeled_kits ? ' · labeled Starlink kits on public bills' : ''}
      </p>
      <dl className="mt-2 space-y-1 text-[12px]">
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Debut</dt>
          <dd className="font-medium text-slate-900">{formatDebut(market.debut)}</dd>
        </div>
        {market.expected ? (
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Expected</dt>
            <dd className="font-medium text-slate-900">{market.expected}</dd>
          </div>
        ) : null}
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Download p50</dt>
          <dd className="font-medium text-slate-900">{formatMbps(market.download_p50)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Latency p50</dt>
          <dd className="font-medium text-slate-900">{formatMs(market.latency_p50)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Ookla share</dt>
          <dd className="font-medium text-slate-900">{formatShare(market.ookla_sample_share)}</dd>
        </div>
        {market.printed_subscribers_m ? (
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Printed customers</dt>
            <dd className="font-medium text-slate-900">{market.printed_subscribers_m.toFixed(1)}M</dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}

export function CoverageStatusBar({ compact = false }: { compact?: boolean }) {
  const payload = useStarlinkMarkets();
  const total = payload.kpis.live + payload.kpis.coming_soon + payload.kpis.pending + payload.kpis.blocked;
  const segments = [
    { id: 'live', value: payload.kpis.live, color: '#157a3a' },
    { id: 'coming_soon', value: payload.kpis.coming_soon, color: '#fbbf24' },
    { id: 'pending', value: payload.kpis.pending, color: '#94a3b8' },
    { id: 'blocked', value: payload.kpis.blocked, color: '#f43f5e' },
  ];
  return (
    <div className={compact ? 'flex h-full flex-col justify-end' : ''}>
      <div className="flex h-8 overflow-hidden rounded-md bg-slate-100">
        {segments.map((segment) => (
          <div
            key={segment.id}
            style={{
              width: total ? `${(segment.value / total) * 100}%` : 0,
              background: segment.color,
            }}
            title={`${BUCKET_LABEL[segment.id as MarketBucket]} ${segment.value}`}
          />
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-3 text-[10px] text-slate-500">
        {segments.map((segment) => (
          <li key={segment.id} className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm" style={{ background: segment.color }} />
            {BUCKET_LABEL[segment.id as MarketBucket]} {segment.value}
          </li>
        ))}
      </ul>
      {compact ? null : (
        <p className="mt-2 text-[11px] text-slate-400">
          Official admin0 file. MD&A prints {payload.printed.markets_mda} markets; availability.json has{' '}
          {payload.kpis.live} live.
        </p>
      )}
    </div>
  );
}

function bucketTone(bucket: MarketBucket): { bg: string; fg: string } {
  if (bucket === 'live') return { bg: '#157a3a', fg: '#ffffff' };
  if (bucket === 'coming_soon') return { bg: '#fbbf24', fg: '#422006' };
  if (bucket === 'pending') return { bg: '#e2e8f0', fg: '#334155' };
  if (bucket === 'blocked') return { bg: '#f43f5e', fg: '#ffffff' };
  return { bg: '#cbd5e1', fg: '#334155' };
}

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}
